import { Field, useFormikContext } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SquareFormValues } from '@/utils/forms/squareTransformers';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Upload, Loader2, ImagePlus, Search, RefreshCcw } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useUploadAssetMutation } from '@/api/squares/uploadAsset';
import { useGetTenantImageLibraryQuery } from '@/api/tenants/tenantImageLibrary';
import { MAX_UPLOAD_SIZE_MB, MAX_UPLOAD_SIZE_BYTES } from '@low6-pickem/ui';

interface CustomAssetsSectionProps {
  disabled?: boolean;
  contestId?: string; // Squares contest ID for uploading assets
  tenantCode?: string;
}

interface AssetConfig {
  name: keyof SquareFormValues;
  label: string;
  description: string;
  width: number;
  height: number;
}

const ASSET_CONFIGS: AssetConfig[] = [
  {
    name: 'badge_image_url',
    label: 'Badge Image',
    description: 'Sponsor logo/badge displayed in game header and rules view',
    width: 212.02,
    height: 216.69,
  },
  {
    name: 'background_image_url',
    label: 'Background Image',
    description: 'Background image for the play view header',
    width: 375,
    height: 161,
  },
  {
    name: 'rules_image_url',
    label: 'Rules Image',
    description: 'Banner background image for the rules view',
    width: 375,
    height: 351,
  },
  {
    name: 'submit_view_image_url',
    label: 'Submit View Image',
    description: 'Promotional image shown in the submission confirmation modal',
    width: 279,
    height: 170,
  },
];

// Map field names to asset types
const FIELD_TO_ASSET_TYPE: Record<string, 'badge' | 'background' | 'rules' | 'submit-view'> = {
  badge_image_url: 'badge',
  background_image_url: 'background',
  rules_image_url: 'rules',
  submit_view_image_url: 'submit-view',
};

function getDisplayName(blobName: string) {
  const segments = blobName.split('/');
  return segments[segments.length - 1] || blobName;
}

function formatFileSize(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function CustomAssetsSection({
  disabled = false,
  contestId,
  tenantCode,
}: CustomAssetsSectionProps) {
  const { values, setFieldValue } = useFormikContext<SquareFormValues>();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(new Set());
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryFieldName, setLibraryFieldName] = useState<keyof SquareFormValues | null>(null);
  const uploadMutation = useUploadAssetMutation();
  const {
    data: imageLibrary,
    isLoading: isImageLibraryLoading,
    isFetching: isImageLibraryFetching,
    isError: isImageLibraryError,
    refetch: refetchImageLibrary,
  } = useGetTenantImageLibraryQuery(tenantCode);

  const libraryImages = useMemo(() => imageLibrary?.images ?? [], [imageLibrary?.images]);
  const filteredLibraryImages = useMemo(() => {
    const query = librarySearch.trim().toLowerCase();
    if (!query) return libraryImages;
    return libraryImages.filter(image => {
      const name = getDisplayName(image.name).toLowerCase();
      return name.includes(query) || image.name.toLowerCase().includes(query);
    });
  }, [libraryImages, librarySearch]);

  const activeLibraryAsset = useMemo(
    () => ASSET_CONFIGS.find(config => config.name === libraryFieldName),
    [libraryFieldName]
  );

  const handleFileUpload = async (fieldName: keyof SquareFormValues, file: File | null) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      alert(`Image file size must be less than ${MAX_UPLOAD_SIZE_MB}MB`);
      return;
    }

    // Check if contest ID is available
    if (!contestId) {
      alert('Please save the contest first before uploading images');
      return;
    }

    // Get asset type from field name
    const assetType = FIELD_TO_ASSET_TYPE[fieldName as string];
    if (!assetType) {
      alert('Invalid asset type');
      return;
    }

    // Convert to data URL for preview and upload
    const reader = new FileReader();
    reader.onload = async e => {
      const dataUrl = e.target?.result as string;

      // Show preview immediately
      setFieldValue(fieldName as string, dataUrl);

      // Upload to backend
      setUploadingFields(prev => new Set(prev).add(fieldName as string));
      try {
        const result = await uploadMutation.mutateAsync({
          contestId,
          assetType,
          imageData: dataUrl,
        });

        // Replace data URL with the uploaded URL
        setFieldValue(fieldName as string, result.imageUrl);
      } catch (error) {
        // Keep the data URL as fallback if upload fails
        console.error('Failed to upload image:', error);
      } finally {
        setUploadingFields(prev => {
          const next = new Set(prev);
          next.delete(fieldName as string);
          return next;
        });
      }
    };
    reader.onerror = () => {
      alert('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleSelectFromLibrary = (imageUrl: string) => {
    if (!libraryFieldName) return;
    setFieldValue(libraryFieldName as string, imageUrl);
    setLibraryFieldName(null);
    setLibrarySearch('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Custom Background Assets</h3>
        <p className="text-sm text-muted-foreground">
          Upload custom promotional images for different views. Images will be displayed at the
          specified dimensions.
        </p>
      </div>

      <div className="space-y-6">
        {ASSET_CONFIGS.map(config => {
          const imageUrl = values[config.name] as string | undefined;
          const fieldName = config.name as string;

          return (
            <div key={fieldName} className="border rounded-lg p-4 space-y-4">
              <div>
                <h4 className="font-medium">{config.label}</h4>
                <p className="text-xs text-muted-foreground mt-1">{config.description}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Recommended size: {config.width}px × {config.height}px
                </p>
              </div>

              {/* Image Preview */}
              {imageUrl && (
                <div className="border rounded-lg overflow-hidden bg-gray-50">
                  <div
                    className="relative"
                    style={{
                      width: `${Math.min(config.width, 300)}px`,
                      height: `${Math.min(config.height, 300)}px`,
                      margin: '0 auto',
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={config.label}
                      className="w-full h-full object-contain"
                      onError={e => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}

              {/* URL Input and Upload Button */}
              <div className="flex flex-col gap-2">
                <Label htmlFor={fieldName}>Image URL</Label>
                <div className="flex gap-2">
                  <Field
                    as={Input}
                    id={fieldName}
                    name={fieldName}
                    placeholder="https://example.com/image.png"
                    disabled={disabled}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || !tenantCode}
                    onClick={() => setLibraryFieldName(config.name)}
                    title={
                      tenantCode
                        ? 'Select from tenant image library'
                        : 'Select a tenant first to use image library'
                    }
                  >
                    <ImagePlus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || uploadingFields.has(fieldName) || !contestId}
                    onClick={() => fileInputRefs.current[fieldName]?.click()}
                    title={!contestId ? 'Please save the contest first' : 'Upload custom image'}
                  >
                    {uploadingFields.has(fieldName) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                  <input
                    ref={el => {
                      fileInputRefs.current[fieldName] = el;
                    }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0] || null;
                      handleFileUpload(config.name, file);
                      // Reset input so same file can be selected again
                      if (e.target) {
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter a URL, pick from Image Library, or upload an image file. Uploaded images
                  will be saved to cloud storage.
                  {!tenantCode && ' (Select a tenant to enable image library)'}
                  {!contestId && ' (Save the contest first to enable upload)'}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog
        open={!!libraryFieldName}
        onOpenChange={open => {
          if (!open) {
            setLibraryFieldName(null);
            setLibrarySearch('');
          }
        }}
      >
        <DialogContent className="max-w-5xl p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Select Image from Library</DialogTitle>
            <DialogDescription>
              {activeLibraryAsset
                ? `Choose an image for ${activeLibraryAsset.label}.`
                : 'Choose an image for this asset.'}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-6 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={librarySearch}
                  onChange={e => setLibrarySearch(e.target.value)}
                  placeholder="Search images by filename..."
                  className="pl-9"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => refetchImageLibrary()}
                disabled={isImageLibraryFetching}
              >
                <RefreshCcw
                  className={isImageLibraryFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                />
                Refresh
              </Button>
            </div>

            <div className="max-h-[58vh] overflow-y-auto border rounded-md p-3">
              {isImageLibraryLoading ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  Loading image library...
                </div>
              ) : isImageLibraryError ? (
                <div className="py-10 text-center text-sm text-red-600">
                  Failed to load image library.
                </div>
              ) : filteredLibraryImages.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  {libraryImages.length === 0
                    ? 'No images found in this tenant image library.'
                    : 'No images match your search.'}
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredLibraryImages.map(image => {
                    const displayName = getDisplayName(image.name);
                    return (
                      <button
                        key={image.name}
                        type="button"
                        className="text-left border rounded-md p-2 hover:border-primary transition-colors"
                        onClick={() => handleSelectFromLibrary(image.url)}
                      >
                        <div className="h-28 w-full overflow-hidden rounded border bg-muted/20 mb-2">
                          <img
                            src={image.url}
                            alt={displayName}
                            className="h-full w-full object-contain"
                            loading="lazy"
                          />
                        </div>
                        <div className="text-xs font-medium truncate" title={displayName}>
                          {displayName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatFileSize(image.size)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
